import React, {Fragment, useEffect, useState} from 'react';
import {Button, Col, Container, Row} from 'react-bootstrap';
import {useParams} from "react-router-dom";
import {
    Conditional
} from "../../Conditional";
import {
    ConfirmationDialog
} from "../../Common/ConfirmationDialog";
import {
    ConservationAction
} from "../../../Models/StreamBank/ConservationAction";
import {
    GenericModelViewer
} from "../../Common/GenericModelViewer";
import {
    Glyphicon
} from "../../Common/GlyphIcon";
import {ObjectUtils} from "../../../Utils";
import {useChangePage} from "../../../Hooks/NavigationHooks";

import {
    useChangeContext,
    useGetQueryStringParameter,
} from "../../../Hooks/RouteHooks";
import {
    useMakePageRequest,
} from "../../../Hooks/RequestHooks";
import {useModeledUpliftConflictHandler} from "../../../Hooks/ErrorHooks";
import {
    useEditContextNodeId,
    usePermission
} from "../../../Hooks/PermissionHooks";

import {service as ConservationActionService} from '../../../Services/ConservationActionService';
import {service as ConservationActionStatisticService} from '../../../Services/ConservationActionStatisticService';
import {service as ConservationActionTypeService} from '../../../Services/ConservationActionTypeService';
import {service as ProjectService} from '../../../Services/ProjectService';


import {useClassNames} from "../../../Hooks/StyleHooks";

import {
    ContextNodeBenefitSummarySection
} from "../../Common/BenefitSummary/ContextNodeBenefitSummarySection";
import {SbMap} from "../../Common/SbMap/SbMap";

import {ConservationActionStatsTable} from "./ConservationActionStatsTable";
import {RecentEvents} from "./RecentEvents";
import {useValidations} from "../../../Hooks/GenericModelHooks";

let model = new ConservationAction();

export const ConservationActionsViewPage = props => {
    const classNames = useClassNames('sb-conservation-actions-view-page');
    const getQueryStringParameter = useGetQueryStringParameter();
    const makePageRequest = useMakePageRequest();
    const handleModeledUpliftConflict = useModeledUpliftConflictHandler();
    const permission = usePermission();
    const editContextNodeId = useEditContextNodeId();
    const changeContext = useChangeContext();
    const changePage = useChangePage();

    const [conservationAction, setConservationAction] = useState(null);
    const [conservationActionTypes, setConservationActionTypes] = useState([]);
    const [existingConservationAction, setExistingConservationAction] = useState(null);
    const [validations, setValidations, isValid] = useValidations();
    const [conservationActionStats, setConservationActionStats] = useState([]);
    const [editMode, setEditMode] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState(false);

    const {conActId, projId} = useParams();
    const conservationActionId = conActId;
    const projectId = projId || {};
    const contextNodeId = conservationAction?.contextNodeId;

    useEffect(() => {
        if (conservationActionId) {
            loadData();
        }
    }, [conservationActionId]);

    const loadData = () => {
        //if this is a new conservationAction we should only load a subset of the needed data.
        if(conservationActionId === "new") {
            makePageRequest(
                _ => Promise.all([
                    ConservationActionTypeService.Get(),
                    ProjectService.GetProject(projectId)
                ]),
                ([types, project]) => {
                    let typeId = getQueryStringParameter("type");
                    let type = types.result.find((x) => x.conservationActionTypeId == typeId);

                    setEditMode(true);
                    setConservationActionTypes(types.result);
                    setConservationAction({
                        conservationActionTypeId: type.conservationActionTypeId,
                        Location: {     // that should be defined by our model. //Maybe we can have a factory method on it?
                            datumId: 1,
                            instrumentId: 1,
                            unitId: 1
                        },
                        ConservationActionSampleEvent: [],
                        projectId: project.result.projectId,
                        usesSimplifiedEnvironmentalBenefits: project.result.organizationId === 2 ? 1 : 0 // hardcoded fallback for new CA; matches MySQL integer output from the virtual field
                    });
                }
            );
        } else {
            makePageRequest(
                _ => Promise.all([
                    ConservationActionService.Get(conservationActionId),
                    ConservationActionTypeService.Get(),
                    ConservationActionStatisticService.GetStats(conservationActionId)
                ]),
                ([conservationAction, types, stats]) => {
                    setConservationAction(conservationAction.result);
                    setConservationActionTypes(types.result);
                    setConservationActionStats(stats.result);
                    setEditMode(false);
                }
            );
        }
    };

    const enterEditMode = () => {
        setEditMode(true);
        setExistingConservationAction(ObjectUtils.Clone(conservationAction));
    };

    const exitEditMode = () => {
        if(existingConservationAction) {
            setEditMode(false);
            setConservationAction(ObjectUtils.Clone(existingConservationAction));
            setExistingConservationAction(null);
        } else {
            changeContext?.({conservationAction: null});
        }
    };

    const updateConservationAction = (conservationAction) => {
        setConservationAction(conservationAction);
    };

    const saveConservationAction = () => {
        // modeled uplift is survey-owned: omit the association entirely so this
        // save cannot update, shadow, or delete modeling events (a round-tripped
        // stale copy could otherwise shadow a newer survey/Fulcrum edit) — the
        // server treats an absent key as "association untouched"
        const {ConservationActionSampleEvents, ...conservationActionToSave} = conservationAction;

        if(conservationAction.conservationActionId) {
            makePageRequest(
                () => ConservationActionService.Update(conservationActionToSave),
                (x) => {
                    setEditMode(false);
                    setConservationAction(x.result);
                },
                // SB-3308: a 409 means the modeled uplift changed under us (e.g. a
                // Fulcrum sync) and the save was rolled back. Refresh ONLY the
                // modeled-uplift associations so the user's other in-form edits
                // survive; they review the new values and save again.
                (error) => handleModeledUpliftConflict(error, () => {
                    makePageRequest(
                        () => ConservationActionService.Get(conservationAction.conservationActionId),
                        (x) => {
                            setConservationAction(current => ({
                                ...current,
                                ConservationActionSampleEvents: x.result.ConservationActionSampleEvents,
                                ConservationActionModeledUplift: x.result.ConservationActionModeledUplift,
                            }));
                        }
                    );
                })
            );
        } else {
            makePageRequest(
                () => ConservationActionService.Create(conservationActionToSave),
                (x) => {
                    let newPath = window.location.pathname.replace("new", x.result.conservationActionId);
                    changePage(newPath);
                },
            );
        }
    };

    const deleteConservationAction = () => {
        makePageRequest(
            () => ConservationActionService.Delete(conservationAction.conservationActionId),
            (x) => {
                changeContext({conservationAction: null});
            }
        );
    };

    const getHiddenSections = () => {
        // Modeled Uplift is edited through the phase-scoped surveys, never the
        // summary — the save below also omits the association from its payload
        // so a summary save cannot touch modeling events
        let ret = ["Permits", "Instream Habitat", "Riparian Habitat", "Upland", "Modeled Uplift"];

        if(conservationAction) {
            let conservationActionType = conservationActionTypes.find((x) => x.conservationActionTypeId == conservationAction.conservationActionTypeId);

            if(conservationActionType) {
                ret = ret.filter((x) => x !== conservationActionType.name);
            }
        }

        return ret;
    };

    const canEdit = () => {
        if(conservationAction) {
            if(permission('update_conservation_action')) {
                return editContextNodeId(conservationAction.contextNodeId);
            }
        }
        return false;
    };

    const canDelete = () => {
        if(conservationAction) {
            if(permission('delete_conservation_action')) {
                return editContextNodeId(conservationAction.contextNodeId);
            }
        }

        return false;
    };

    let conservationActionType = null;

    if(conservationAction) {
        conservationActionType = conservationActionTypes.find((x) => x.conservationActionTypeId == conservationAction.conservationActionTypeId);
    }

    // don't render anything until we are loaded.
    if(conservationActionType === null) {
        return null;
    }

    const latitude = conservationAction.Location?.latitude;
    const longitude = conservationAction.Location?.longitude;
    const accuracy = conservationAction.Location?.accuracy;

    return <Container className={classNames}>
        <ConfirmationDialog
            Title="Confirmation"
            Body="Are you sure you want to delete this conservation action?"
            Display={deleteDialog}
            OnClose={() => {
                setDeleteDialog(false);
            }}
            OnConfirm={() => {
                deleteConservationAction();
            }}
        />
        <Row>
            <Col xs={12} md={6}>
                <h2 className="atomic-sectionheader">
                    <div className="float-right btn-edit">
                        {
                            editMode ? [
                                <Button
                                    variant="save"
                                    key={1}
                                    onClick={() => saveConservationAction()}
                                    disabled={!isValid}
                                >
                                    Save
                                </Button>,
                                <Button
                                    variant="cancel"
                                    key={2}
                                    onClick={() => exitEditMode()}
                                >
                                    Cancel
                                </Button>,
                                (conservationActionId !== 'new' && canDelete()) ?
                                    <Button
                                        variant="delete"
                                        key={3}
                                        onClick={() => setDeleteDialog(true)}
                                    >
                                        Delete
                                    </Button> :
                                    null
                            ] :
                            (
                                canEdit() ?
                                    <Button
                                        variant="link"
                                        onClick={() => enterEditMode()}>
                                        <Glyphicon glyph="pencil" />Edit</Button>:
                                    null
                            )
                        }
                    </div>
                    <span className="sb-conservation-action-header">{conservationActionType ? conservationActionType.name: "Conservation Action"}</span>
                    <span className="sb-print-show">{conservationAction?.ContextNodeDetails?.name}</span>
                </h2>

                {
                    conservationAction ?
                        <GenericModelViewer
                            IsNewModel={conservationActionId === "new"}
                            EditMode={editMode}
                            Data={conservationAction}
                            Update={updateConservationAction}
                            OnValidationUpdate={setValidations}
                            Model={model}
                            HideGroups={getHiddenSections()}
                        />:
                        null
                }
                <Conditional Condition={Array.isArray(conservationActionStats) && conservationActionStats.length}>
                    <ConservationActionStatsTable conservationActionStats={conservationActionStats}/>
                </Conditional>
                <ContextNodeBenefitSummarySection contextNodeId={contextNodeId} editMode={editMode} />
            </Col>
            <Col xs={12} md={{span: 5, offset:1}}>
                { 
                    conservationAction?.conservationActionId && permission('view_sample_events') && <RecentEvents contextNodeId={contextNodeId} /> 
                }
                {
                    (conservationAction?.conservationActionId && conservationAction.Location.latitude && conservationAction.Location.longitude) ?
                        <Fragment>
                            <h2 className="sb-children-list-title">Site Location</h2>
                            <SbMap
                                Lat={latitude}
                                Lng={longitude}
                                Zoom={11}
                                Markers={[
                                    {
                                        key: conservationAction.conservationActionId,
                                        lat: latitude,
                                        lng: longitude,
                                        accuracy,
                                        marker: !permission('authenticated_user') ? 'circle' : 'both',
                                        content: <div>{conservationActionType ? conservationActionType.name: "Conservation Action"}<br />{conservationAction.locationDescription}</div>
                                    }
                                ]}
                            />
                        </Fragment>:
                        null
                }
            </Col>
        </Row>
    </Container>;
};
